/**
 * Glossary Component
 *
 * Displays all technical terms and their explanations in a table format.
 */

import { Paper, Typography, Table, TableBody, TableCell, TableContainer, TableRow, Accordion, AccordionSummary, AccordionDetails, TableHead } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TOOLTIPS } from '../../data/tooltips';

export function Glossary() {
  // Group tooltips by category
  const categorizedTooltips: Record<string, typeof TOOLTIPS> = {
    'Storage Topology': {},
    'Drive Parameters': {},
    'ZFS Parameters': {},
    'Reliability Metrics': {},
    'Performance Metrics': {},
    'Economics': {},
    'Capacity': {},
  };

  // Categorize each tooltip
  Object.entries(TOOLTIPS).forEach(([key, def]) => {
    if (['raidLevel', 'vdev', 'drivesPerVdev', 'vdevCount'].includes(key)) {
      categorizedTooltips['Storage Topology'][key] = def;
    } else if (['URE', 'AFR', 'driveIOPS', 'peakSequentialSpeed'].includes(key)) {
      categorizedTooltips['Drive Parameters'][key] = def;
    } else if (['ashift', 'slop', 'recordsize', 'compressionRatio'].includes(key)) {
      categorizedTooltips['ZFS Parameters'][key] = def;
    } else if (['MTTP', 'resilverTime', 'writePenalty', 'maxTolerableFailures', 'vdevFailureProbability', 'poolFailureProbability'].includes(key)) {
      categorizedTooltips['Reliability Metrics'][key] = def;
    } else if (['IOPS', 'randomWriteIOPS', 'randomReadIOPS', 'effectiveIOPS', 'sequentialThroughput', 'bottleneck'].includes(key)) {
      categorizedTooltips['Performance Metrics'][key] = def;
    } else if (['costPerTiB', 'usableTiB', 'totalWatts', 'annualKWh', 'tco', 'expectedFailures'].includes(key)) {
      categorizedTooltips['Economics'][key] = def;
    } else if (['poolFillPercent', 'rawCapacity', 'parityOverhead', 'zfsOverhead'].includes(key)) {
      categorizedTooltips['Capacity'][key] = def;
    } else {
      // Default to Drive Parameters if not categorized
      categorizedTooltips['Drive Parameters'][key] = def;
    }
  });

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Technical Terms Glossary
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Click on any category below to expand and view detailed explanations of technical terms used throughout the ZFS Calculator.
      </Typography>

      {Object.entries(categorizedTooltips).map(([category, tooltips]) => (
        Object.keys(tooltips).length > 0 && (
          <Accordion key={category} defaultExpanded={category === 'Storage Topology'}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">{category}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: '30%', fontWeight: 'bold' }}>Term</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Explanation</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(tooltips).map(([key, def]) => (
                      <TableRow key={key}>
                        <TableCell component="th" scope="row">
                          <strong>{def.term}</strong>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{def.explanation}</Typography>
                          {def.detailedInfo && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {def.detailedInfo}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )
      ))}
    </Paper>
  );
}
